import React, { useState, useMemo } from 'react';
import { RotateCcw, Home, Sparkles, Copy, Check, ChevronDown, ChevronUp, AlertTriangle, LifeBuoy, ArrowLeft } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { db, collection, addDoc } from '../services/firebase';
import logo from '../assets/logo.png';

const DEV_SUPPORT_PHONE = '8051725780';

// Safe transient cache cleanup without destroying user session
const clearTransientCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('lg_cache_') || k.includes('cache') || k.includes('temp') || k.includes('dismissed'))) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    sessionStorage.clear();
  } catch {}
  window.location.reload();
};

// Clean incident reference code
const createIncidentCode = () => {
  const hex = Math.random().toString(16).substring(2, 6).toUpperCase();
  const dateStr = new Date().toISOString().slice(2, 10).replace(/-/g, '');
  return `LG-${dateStr}-${hex}`;
};

/**
 * Enterprise & Mobile-First Crash Screen
 * Clean, modern, calm, reassuring, and aligned with Lucy Garden enterprise design.
 */
export function EnterpriseCrashScreen({ error }) {
  const [incidentCode] = useState(createIncidentCode);
  const [copied, setCopied] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const [clearing, setClearing] = useState(false);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('lg_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const errorMessage = error?.message || 'An unexpected runtime error occurred';
  const homeUrl = currentUser?.role === 'admin' ? '/admin' : '/';

  const handleCopy = () => {
    try {
      const text = `Lucy Garden Incident: ${incidentCode}\nURL: ${window.location.href}\nUser: ${currentUser?.phone || 'Guest'}\nError: ${errorMessage}\nTimestamp: ${new Date().toISOString()}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleClearCache = () => {
    setClearing(true);
    setTimeout(clearTransientCache, 300);
  };

  const whatsAppUrl = useMemo(() => {
    const text = encodeURIComponent(
      `Hello Lucy Garden Support, my app encountered an issue:\n\n` +
      `• Incident ID: ${incidentCode}\n` +
      `• User: ${currentUser?.name || currentUser?.phone || 'Not logged in'}\n` +
      `• Page: ${window.location.pathname}\n` +
      `• Error: ${errorMessage.slice(0, 120)}\n\n` +
      `Please help verify.`
    );
    return `https://wa.me/91${DEV_SUPPORT_PHONE}?text=${text}`;
  }, [incidentCode, currentUser, errorMessage]);

  return (
    <div className="min-h-screen bg-[#f8f7f4] dark:bg-[#11100f] text-[#1a1917] dark:text-[#f5f4f0] flex flex-col justify-between p-4 sm:p-6 font-sans antialiased selection:bg-[#1e3a5f]/15">
      
      {/* Background ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#1e3a5f]/4 dark:bg-[#93c5fd]/3 rounded-full blur-3xl" />
      </div>

      {/* Top Brand Bar */}
      <header className="relative z-10 w-full max-w-lg mx-auto flex items-center justify-between py-2">
        <div className="flex items-center gap-2.5">
          <img
            src={logo}
            alt="Lucy Garden"
            className="w-8 h-8 rounded-lg object-cover ring-1 ring-black/10 dark:ring-white/10"
          />
          <div className="text-left">
            <span className="text-sm font-bold tracking-tight text-[#1a1917] dark:text-white leading-none block">
              Lucy Garden
            </span>
            <span className="text-[10px] text-[#6b6860] dark:text-[#a8a49c] font-medium leading-none mt-0.5 block">
              Fresh Dairy Supply
            </span>
          </div>
        </div>

        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono font-semibold bg-[#fee2e2]/70 dark:bg-[#450a0a]/50 text-[#991b1b] dark:text-[#f87171] border border-[#fecaca] dark:border-[#7f1d1d]/60">
          <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse" />
          Recovery Mode
        </span>
      </header>

      {/* Main Recovery Card */}
      <main className="relative z-10 w-full max-w-lg mx-auto my-auto py-4">
        <div className="bg-white dark:bg-[#181716] border border-[#e2e0db] dark:border-[#2e2d2b] rounded-2xl p-5 sm:p-7 shadow-xl shadow-black/[0.03]">
          
          {/* Status Header */}
          <div className="flex items-start gap-4 mb-5">
            <div className="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-400 flex items-center justify-center shrink-0 shadow-2xs">
              <AlertTriangle size={22} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <h1 className="text-base sm:text-lg font-bold text-[#1a1917] dark:text-white tracking-tight leading-snug">
                Something unexpected happened
              </h1>
              <p className="text-xs text-[#6b6860] dark:text-[#a8a49c] mt-1 leading-relaxed">
                The application encountered a display glitch. Your database records, ledger balances, and active session are completely safe.
              </p>
            </div>
          </div>

          {/* Quick Actions Group */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full py-2.5 px-4 bg-[#1e3a5f] hover:bg-[#162d4a] active:scale-[0.98] text-white font-semibold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-2"
            >
              <RotateCcw size={14} />
              <span>Reload Application</span>
            </button>

            <button
              type="button"
              onClick={() => { window.location.href = homeUrl; }}
              className="w-full py-2.5 px-4 bg-transparent hover:bg-[#f8f7f4] dark:hover:bg-[#22211f] active:scale-[0.98] text-[#1a1917] dark:text-[#f5f4f0] font-semibold text-xs rounded-xl border border-[#e2e0db] dark:border-[#2e2d2b] transition-all flex items-center justify-center gap-2"
            >
              <Home size={14} />
              <span>Return to Dashboard</span>
            </button>
          </div>

          {/* Self-Healing & Diagnostics Bar */}
          <div className="bg-[#f8f7f4] dark:bg-[#201f1d] border border-[#e8e6e1] dark:border-[#2a2927] rounded-xl p-3 space-y-2.5">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-[#6b6860] dark:text-[#a8a49c]">
                <Sparkles size={13} className="text-amber-500 shrink-0" />
                <span className="font-medium text-[11px]">Having trouble after reload?</span>
              </div>
              <button
                type="button"
                onClick={handleClearCache}
                disabled={clearing}
                className="px-2.5 py-1 text-[11px] font-semibold text-[#1e3a5f] dark:text-[#93c5fd] bg-white dark:bg-[#181716] hover:bg-[#e2e0db]/50 border border-[#e2e0db] dark:border-[#2e2d2b] rounded-lg transition-colors disabled:opacity-50"
              >
                {clearing ? 'Clearing...' : 'Clear Temp Cache'}
              </button>
            </div>

            {/* Collapsible Technical Details */}
            <div className="border-t border-[#e8e6e1] dark:border-[#2a2927] pt-2">
              <button
                type="button"
                onClick={() => setShowTechnical(!showTechnical)}
                className="w-full flex items-center justify-between text-[11px] font-medium text-[#8c887e] dark:text-[#6b6860] hover:text-[#1a1917] dark:hover:text-white transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  <span>Incident Reference:</span>
                  <span className="font-mono font-bold text-[#1e3a5f] dark:text-[#93c5fd]">{incidentCode}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span>{showTechnical ? 'Hide details' : 'View details'}</span>
                  {showTechnical ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </span>
              </button>

              {showTechnical && (
                <div className="mt-2.5 p-2.5 rounded-lg bg-white dark:bg-[#151413] border border-[#e2e0db] dark:border-[#282725] text-[11px] space-y-2 text-left animate-fade-in">
                  <div className="flex items-center justify-between text-[10px] text-[#8c887e]">
                    <span>PAGE: {window.location.pathname}</span>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="px-2 py-0.5 rounded font-medium hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1 text-[#1e3a5f] dark:text-[#93c5fd]"
                    >
                      {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                      <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <p className="font-mono text-[10px] text-red-600 dark:text-red-400 break-words leading-relaxed max-h-24 overflow-y-auto">
                    {errorMessage}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Discreet Help & Support Row */}
          <div className="mt-4 pt-3 border-t border-[#f0eee9] dark:border-[#252422] flex items-center justify-between text-xs text-[#8c887e] dark:text-[#6b6860]">
            <span className="text-[11px]">Need immediate assistance?</span>
            <div className="flex items-center gap-3">
              <a
                href={whatsAppUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-[#166534] dark:text-[#4ade80] hover:underline text-[11px]"
              >
                <FaWhatsapp size={13} />
                <span>WhatsApp</span>
              </a>
              <span className="text-[#d3d0c9] dark:text-[#383633]">·</span>
              <a
                href={`tel:+91${DEV_SUPPORT_PHONE}`}
                className="inline-flex items-center gap-1 font-medium text-[#1e3a5f] dark:text-[#93c5fd] hover:underline text-[11px]"
              >
                <span>Call Support</span>
              </a>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full max-w-lg mx-auto py-2 text-center text-[10px] text-[#8c887e] dark:text-[#6b6860]">
        Lucy Garden Portal • Powered by Makeward Labs
      </footer>
    </div>
  );
}

// ROOT level — catches EVERYTHING (Login, providers, etc.)
export class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      addDoc(collection(db, 'app_errors'), {
        message: error?.message || 'Root crash',
        stack: error?.stack || '',
        componentStack: errorInfo?.componentStack || '',
        url: window.location.href,
        user: JSON.parse(localStorage.getItem('lg_user') || '{}')?.phone || 'unknown',
        timestamp: new Date().toISOString(),
        type: 'root_crash',
        severity: 'critical',
      }).catch(() => {});
    } catch (e) {}
  }

  render() {
    if (this.state.hasError) {
      return <EnterpriseCrashScreen error={this.state.error} />;
    }
    return this.props.children;
  }
}

// APP level — wraps RetailerLayout and AdminLayout
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      addDoc(collection(db, 'app_errors'), {
        message: error?.message || 'Unknown error',
        stack: error?.stack || '',
        componentStack: errorInfo?.componentStack || '',
        url: window.location.href,
        user: JSON.parse(localStorage.getItem('lg_user') || '{}')?.phone || 'unknown',
        timestamp: new Date().toISOString(),
        type: 'crash',
        severity: 'high',
        context: 'ErrorBoundary',
      }).catch(() => {});
    } catch (e) {}
  }

  render() {
    if (this.state.hasError) {
      return <EnterpriseCrashScreen error={this.state.error} />;
    }
    return this.props.children;
  }
}

// DEV console error boundary
export class DevErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      addDoc(collection(db, 'app_errors'), {
        message: error?.message || 'Dev console crash',
        stack: error?.stack || '',
        componentStack: errorInfo?.componentStack || '',
        url: window.location.href,
        user: '8051725780',
        timestamp: new Date().toISOString(),
        type: 'dev_crash',
        severity: 'medium',
        context: 'DevErrorBoundary',
      }).catch(() => {});
    } catch (e) {}
  }

  render() {
    if (this.state.hasError) {
      return <EnterpriseCrashScreen error={this.state.error} />;
    }
    return this.props.children;
  }
}
